import React, { useMemo } from 'react';
import { Copy, Check, Hash } from 'lucide-react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';
import { SOCIAL_CHANNELS } from '../../constants/channels.js';

const YT_COLOR =
  (SOCIAL_CHANNELS.find((c) => c.id === 'youtube') || {}).color || '#FF0000';

const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const renderTag = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  return s.startsWith('#') ? s : `#${s}`;
};

const SectionLabel = ({ children, color, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: color || t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

const CopyBtn = ({ active, onClick, label, t, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '10.5px',
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${t.border || 'rgba(0,0,0,0.1)'}`,
      background: 'transparent',
      color: color || t.textMuted || '#7a7a8e',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    }}
  >
    {active ? <><Check size={11} /> Copiato</> : <><Copy size={11} /> {label}</>}
  </button>
);

const YouTubeResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = YT_COLOR;
  const eId = entry.id || 'youtube';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;

  const title = (c.title || '').trim();
  const titleVariants = Array.isArray(c.title_variants) ? c.title_variants.filter(Boolean) : [];
  const descriptionHook = (c.description_hook || '').trim();
  const description = (c.description || '').trim();
  const chapters = Array.isArray(c.chapters) ? c.chapters.filter(Boolean) : [];
  const tags = Array.isArray(c.tags) ? c.tags.filter(Boolean) : [];
  const thumbnailConcept = (c.thumbnail_concept || '').trim();
  const ctaScript = (c.cta_script || '').trim();
  const seo = c.seo_keywords || {};
  const seoPrimary = (seo.primary || '').trim();
  const seoSecondary = Array.isArray(seo.secondary) ? seo.secondary.filter(Boolean) : [];
  const seoLongTail = Array.isArray(seo.long_tail) ? seo.long_tail.filter(Boolean) : [];
  const hashtags = Array.isArray(c.hashtags) ? c.hashtags.filter(Boolean) : [];

  const descParas = useMemo(() => splitParagraphs(description), [description]);
  const hasSeo = seoPrimary || seoSecondary.length > 0 || seoLongTail.length > 0;

  return (
    <div>
      {/* Region 1 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 2 — Title (bold, no label) */}
      {title && (
        <div style={{ marginBottom: '6px' }}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: t.text,
              lineHeight: 1.35,
            }}
          >
            {title}
          </div>
        </div>
      )}

      {/* Region 3 — ALT N chips for title_variants */}
      {titleVariants.length > 0 && (
        <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {titleVariants.map((v, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: `${color}15`,
                  color,
                  fontSize: '10px',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  flexShrink: 0,
                }}
              >
                ALT {i + 1}
              </span>
              <span style={{ fontSize: '13px', color: t.textMuted || '#7a7a8e', lineHeight: 1.5 }}>
                {v}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Region 4 — Hook descrizione box */}
      {descriptionHook && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 14px',
              borderRadius: '10px',
              background: `${color}08`,
              borderLeft: `3px solid ${color}`,
            }}
          >
            <SectionLabel color={color} t={t}>Hook descrizione</SectionLabel>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: 1.55,
                color: t.text,
                whiteSpace: 'pre-wrap',
              }}
            >
              {descriptionHook}
            </div>
          </div>
        </div>
      )}

      {/* Region 5 — Description body (no label, flat prose) */}
      {descParas.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '6px',
            }}
          >
            <CopyBtn
              active={copiedId === `yt-desc-${eId}`}
              onClick={() => onCopy?.(description, `yt-desc-${eId}`)}
              label="Copia descrizione"
              t={t}
              color={color}
            />
          </div>
          <div style={{ color: t.text }}>
            {descParas.map((p, i) => (
              <p
                key={i}
                style={{
                  margin: i === descParas.length - 1 ? 0 : '0 0 10px',
                  fontSize: '14px',
                  lineHeight: 1.7,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Region 6 — Capitoli (two-column timestamp + title) */}
      {chapters.length > 0 && (
        <div
          style={{
            marginBottom: '14px',
            padding: '14px 16px',
            background: w(0.02),
            border: `1px solid ${w(0.06)}`,
            borderRadius: '10px',
          }}
        >
          <SectionLabel t={t}>Capitoli</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {chapters.map((ch, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', fontSize: '13px', lineHeight: 1.5 }}>
                <code
                  style={{
                    color,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12.5px',
                    fontWeight: 700,
                    flexShrink: 0,
                    minWidth: '46px',
                  }}
                >
                  {ch.timestamp}
                </code>
                <span style={{ color: t.text }}>{ch.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Region 7 — Tag video chips */}
      {tags.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <SectionLabel t={t}>Tag video ({tags.length})</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {tags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '3px 10px',
                  borderRadius: '6px',
                  background: w(0.04),
                  border: `1px solid ${w(0.06)}`,
                  color: t.text,
                  fontSize: '12px',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Region 8 — Thumbnail + CTA Script side-by-side */}
      {(thumbnailConcept || ctaScript) && (
        <div
          style={{
            marginBottom: '14px',
            display: 'grid',
            gridTemplateColumns: thumbnailConcept && ctaScript ? '1fr 1fr' : '1fr',
            gap: '12px',
          }}
        >
          {thumbnailConcept && (
            <div
              style={{
                padding: '12px 14px',
                background: w(0.02),
                border: `1px solid ${w(0.06)}`,
                borderRadius: '10px',
              }}
            >
              <SectionLabel t={t}>Thumbnail</SectionLabel>
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: 1.6,
                  color: t.text,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {thumbnailConcept}
              </div>
            </div>
          )}
          {ctaScript && (
            <div
              style={{
                padding: '12px 14px',
                background: w(0.02),
                border: `1px solid ${w(0.06)}`,
                borderRadius: '10px',
              }}
            >
              <SectionLabel t={t}>CTA script</SectionLabel>
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                  color: t.text,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {ctaScript}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Region 9 — SEO keywords (3-tier) */}
      {hasSeo && (
        <div
          style={{
            marginBottom: '14px',
            padding: '12px 14px',
            background: `${color}05`,
            border: `1px solid ${color}25`,
            borderRadius: '10px',
          }}
        >
          <SectionLabel color={color} t={t}>SEO Keywords</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {seoPrimary && (
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: '6px',
                  background: `${color}20`,
                  color,
                  fontSize: '12px',
                  fontWeight: 700,
                  border: `1px solid ${color}40`,
                }}
              >
                {seoPrimary}
              </span>
            )}
            {seoSecondary.map((kw, i) => (
              <span
                key={`s-${i}`}
                style={{
                  padding: '3px 10px',
                  borderRadius: '6px',
                  background: w(0.04),
                  border: `1px solid ${w(0.06)}`,
                  color: t.text,
                  fontSize: '12px',
                }}
              >
                {kw}
              </span>
            ))}
            {seoLongTail.map((kw, i) => (
              <span
                key={`l-${i}`}
                style={{
                  padding: '3px 10px',
                  borderRadius: '6px',
                  background: w(0.02),
                  border: `1px dashed ${w(0.10)}`,
                  color: t.textMuted || '#7a7a8e',
                  fontSize: '12px',
                  fontStyle: 'italic',
                }}
              >
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Region 10 — Hashtag chips */}
      {hashtags.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <Hash size={12} color={color} />
            <SectionLabel color={color} t={t}>
              Hashtag ({hashtags.length})
            </SectionLabel>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {hashtags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '3px 10px',
                  borderRadius: '20px',
                  background: `${color}15`,
                  color,
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {renderTag(tag)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Region 11 — Sources cited */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />
    </div>
  );
};

export default YouTubeResultEntry;
